import { useCallback, useEffect, useRef, useState } from "react";
import Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";

/** Minimum and maximum Stage scale enforced by zoom gestures. Exported so
 *  rendering code (e.g. the adaptive grid) can map zoom into other ranges. */
export const MIN_PAN_SCALE = 0.4;
export const MAX_PAN_SCALE = 6;
const MIN_SCALE = MIN_PAN_SCALE;
const MAX_SCALE = MAX_PAN_SCALE;
const WHEEL_STEP = 1.08;
/** Duration of the `ensureVisible` glide. */
const ENSURE_VISIBLE_MS = 220;
/** Breathing room kept between a revealed rect and the covered edge, px. */
const ENSURE_VISIBLE_MARGIN_PX = 16;

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

/** Sanitize a Stage transform — drop any non-finite component back to its
 *  identity default. Without this guard, a transient divide-by-zero in
 *  the wheel/pinch handlers (when a previous state somehow had `scale: 0`)
 *  can produce `Infinity`, which then turns into `NaN` on the next event
 *  via `Infinity - Infinity`. A NaN here propagates through `useFitToRoom`
 *  into the canvas's grid loop and freezes the tab — see the
 *  `Number.isFinite` guard in `RoomCanvas.tsx > buildGrid`. */
function sanitizeView(v: { scale: number; x: number; y: number }): {
  scale: number;
  x: number;
  y: number;
} {
  return {
    scale: Number.isFinite(v.scale) && v.scale > 0 ? v.scale : 1,
    x: Number.isFinite(v.x) ? v.x : 0,
    y: Number.isFinite(v.y) ? v.y : 0,
  };
}

function dist(p1: { x: number; y: number }, p2: { x: number; y: number }) {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}

function center(p1: { x: number; y: number }, p2: { x: number; y: number }) {
  return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
}

export interface ScreenInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface StageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Pan (in screen px) that brings `rect` — already in screen px — inside the
 * viewport minus `insets`. Zero when it's already visible. A rect larger
 * than the visible area is centered in it instead.
 */
export function revealDelta(
  rect: StageRect,
  viewport: { width: number; height: number },
  insets: ScreenInsets,
  margin = ENSURE_VISIBLE_MARGIN_PX
): { dx: number; dy: number } {
  const left = insets.left + margin;
  const top = insets.top + margin;
  const right = viewport.width - insets.right - margin;
  const bottom = viewport.height - insets.bottom - margin;
  const axis = (lo: number, hi: number, a: number, b: number) => {
    if (hi <= lo) return 0;
    if (b - a > hi - lo) return (lo + hi) / 2 - (a + b) / 2;
    if (a < lo) return lo - a;
    if (b > hi) return hi - b;
    return 0;
  };
  return {
    dx: axis(left, right, rect.x, rect.x + rect.width),
    dy: axis(top, bottom, rect.y, rect.y + rect.height),
  };
}

export interface StagePanZoomOptions {
  /**
   * Fired once when a second finger lands. Canvases revert whatever edit the
   * first finger had started (a furniture drag, a wall slide, …) so a pinch
   * never leaves a nudge behind. Runs before Konva drags are stopped.
   */
  onInterrupt?: () => void;
}

export interface StagePanZoom {
  /** Attach this to <Stage ref={...} />. */
  stageRef: React.MutableRefObject<Konva.Stage | null>;
  scale: number;
  x: number;
  y: number;
  /** True once the user has zoomed or panned away from the fitted default. */
  isAdjusted: boolean;
  reset: () => void;
  /** Shift the viewport by a small screen-space delta. */
  panBy: (dx: number, dy: number) => void;
  /**
   * Glide the viewport just enough to bring `rect` (stage-local px, i.e. the
   * same space as `fit.pointToPx`) out from under `insets` (screen px).
   * No-op when it's already visible.
   */
  ensureVisible: (rect: StageRect, insets: ScreenInsets) => void;
  /** True while two fingers are down. */
  isPinching: () => boolean;
  /** Bind to <Stage onWheel={...}>. */
  onWheel: (e: KonvaEventObject<WheelEvent>) => void;
  /** Bind to <Stage onTouchStart={...}>. Returns true when a pinch began. */
  onTouchStart: (e: KonvaEventObject<TouchEvent>) => boolean;
  /**
   * Bind to <Stage onTouchMove={...}>. Returns true if it consumed the event
   * (two-finger pinch), so callers can short-circuit any single-finger logic.
   */
  onTouchMove: (e: KonvaEventObject<TouchEvent>) => boolean;
  /** Bind to <Stage onTouchEnd={...}>. */
  onTouchEnd: (e: KonvaEventObject<TouchEvent>) => void;
  /** Bind to <Stage onDragEnd={...}>. Syncs panned position back to state. */
  onDragEnd: (e: KonvaEventObject<DragEvent>) => void;
  /** Bind to <Stage onDragMove={...}> so we don't fight Konva's internal pos. */
  onDragMove: (e: KonvaEventObject<DragEvent>) => void;
}

/**
 * Stop every Konva drag on this stage and forget armed-but-unmoved ones.
 * A "ready" drag element (finger down, under the drag distance) would
 * otherwise start on the next `touchmove` and fight the pinch.
 */
export function cancelKonvaDrags(stage: Konva.Stage) {
  if (stage.isDragging()) stage.stopDrag();
  stage.find((n: Konva.Node) => n.isDragging()).forEach((n) => {
    try {
      (n as Konva.Node & { stopDrag(): void }).stopDrag();
    } catch {
      /* noop */
    }
  });
  const pending = Konva.DD?._dragElements;
  pending?.forEach((elem, key) => {
    if (elem.dragStatus === "ready") pending.delete(key);
  });
  // A finger lifted after a pinch must not register as a tap on whatever
  // ended up under it.
  Konva._touchListenClick = false;
  Konva._pointerListenClick = false;
  Konva._mouseListenClick = false;
}

/**
 * Adds wheel zoom, two-finger pinch zoom, and single-finger drag panning to a
 * Konva Stage. The stage should be set `draggable`; this hook will sync the
 * dragged position back into React state.
 */
export function useStagePanZoom(options: StagePanZoomOptions = {}): StagePanZoom {
  const stageRef = useRef<Konva.Stage | null>(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const lastCenterRef = useRef<{ x: number; y: number } | null>(null);
  const lastDistRef = useRef(0);
  const pinchingRef = useRef(false);
  const onInterruptRef = useRef(options.onInterrupt);
  const glideRef = useRef<number | null>(null);

  useEffect(() => {
    onInterruptRef.current = options.onInterrupt;
  });

  const stopGlide = useCallback(() => {
    if (glideRef.current != null) cancelAnimationFrame(glideRef.current);
    glideRef.current = null;
  }, []);

  useEffect(() => stopGlide, [stopGlide]);

  const reset = useCallback(() => {
    stopGlide();
    setView({ scale: 1, x: 0, y: 0 });
  }, [stopGlide]);

  const panBy = useCallback((dx: number, dy: number) => {
    if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) return;
    setView((prev) => {
      const next = sanitizeView({ ...prev, x: prev.x + dx, y: prev.y + dy });
      // Keep Konva's imperative position in sync immediately so drag math that
      // reads from the stage during the same animation frame sees the pan.
      stageRef.current?.position({ x: next.x, y: next.y });
      return next;
    });
  }, []);

  const ensureVisible = useCallback(
    (rect: StageRect, insets: ScreenInsets) => {
      const stage = stageRef.current;
      if (!stage) return;
      const s = stage.scaleX() || 1;
      const screen = {
        x: rect.x * s + stage.x(),
        y: rect.y * s + stage.y(),
        width: rect.width * s,
        height: rect.height * s,
      };
      const { dx, dy } = revealDelta(
        screen,
        { width: stage.width(), height: stage.height() },
        insets
      );
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      stopGlide();
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion || typeof requestAnimationFrame === "undefined") {
        panBy(dx, dy);
        return;
      }
      const start = performance.now();
      let applied = 0;
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / ENSURE_VISIBLE_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        const delta = eased - applied;
        applied = eased;
        panBy(dx * delta, dy * delta);
        glideRef.current = t < 1 ? requestAnimationFrame(step) : null;
      };
      glideRef.current = requestAnimationFrame(step);
    },
    [panBy, stopGlide]
  );

  const onWheel = useCallback((e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    stopGlide();
    const factor = e.evt.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
    setView((prev) => {
      const newScale = clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE);
      // Convert pointer (canvas-relative px) into the stage's pre-transform
      // coords so we can zoom around it.
      const pointTo = {
        x: (pointer.x - prev.x) / prev.scale,
        y: (pointer.y - prev.y) / prev.scale,
      };
      return sanitizeView({
        scale: newScale,
        x: pointer.x - pointTo.x * newScale,
        y: pointer.y - pointTo.y * newScale,
      });
    });
  }, [stopGlide]);

  /** Enter pinch mode: revert the first finger's edit, cancel drags, seed. */
  const beginPinch = useCallback(
    (p1: { x: number; y: number }, p2: { x: number; y: number }) => {
      const stage = stageRef.current;
      if (!pinchingRef.current) {
        pinchingRef.current = true;
        stopGlide();
        onInterruptRef.current?.();
      }
      if (stage) cancelKonvaDrags(stage);
      lastCenterRef.current = center(p1, p2);
      lastDistRef.current = dist(p1, p2);
    },
    [stopGlide]
  );

  const touchPoints = useCallback((e: KonvaEventObject<TouchEvent>) => {
    const touches = e.evt.touches;
    const t1 = touches?.[0];
    const t2 = touches?.[1];
    const stage = stageRef.current;
    if (!t1 || !t2 || !stage) return null;
    const rect = stage.container().getBoundingClientRect();
    return {
      p1: { x: t1.clientX - rect.left, y: t1.clientY - rect.top },
      p2: { x: t2.clientX - rect.left, y: t2.clientY - rect.top },
    };
  }, []);

  const onTouchStart = useCallback(
    (e: KonvaEventObject<TouchEvent>): boolean => {
      const pts = touchPoints(e);
      if (!pts) return false;
      if (e.evt.cancelable) e.evt.preventDefault();
      beginPinch(pts.p1, pts.p2);
      return true;
    },
    [beginPinch, touchPoints]
  );

  const onTouchMove = useCallback(
    (e: KonvaEventObject<TouchEvent>): boolean => {
      const pts = touchPoints(e);
      if (!pts) return false;
      e.evt.preventDefault();
      const stage = stageRef.current;
      if (!stage) return true;

      // Normally seeded on touchstart; covers a second finger that landed
      // before this Stage mounted or outside its container.
      if (!pinchingRef.current || !lastCenterRef.current) {
        beginPinch(pts.p1, pts.p2);
        return true;
      }
      cancelKonvaDrags(stage);

      const newCenter = center(pts.p1, pts.p2);
      const newDist = dist(pts.p1, pts.p2);
      const prevCenter = lastCenterRef.current;
      const prevDist = lastDistRef.current || newDist;

      setView((prev) => {
        const pointTo = {
          x: (newCenter.x - prev.x) / prev.scale,
          y: (newCenter.y - prev.y) / prev.scale,
        };
        const newScale = clamp(
          prev.scale * (newDist / prevDist),
          MIN_SCALE,
          MAX_SCALE
        );
        const dx = newCenter.x - prevCenter.x;
        const dy = newCenter.y - prevCenter.y;
        return sanitizeView({
          scale: newScale,
          x: newCenter.x - pointTo.x * newScale + dx,
          y: newCenter.y - pointTo.y * newScale + dy,
        });
      });

      lastCenterRef.current = newCenter;
      lastDistRef.current = newDist;
      return true;
    },
    [beginPinch, touchPoints]
  );

  const onTouchEnd = useCallback((e: KonvaEventObject<TouchEvent>) => {
    lastCenterRef.current = null;
    lastDistRef.current = 0;
    // Stay in pinch mode until every finger lifts so the finger left behind
    // doesn't get treated as a fresh one-finger gesture.
    if (!e.evt.touches || e.evt.touches.length === 0) pinchingRef.current = false;
  }, []);

  const isPinching = useCallback(() => pinchingRef.current, []);

  const onDragEnd = useCallback((e: KonvaEventObject<DragEvent>) => {
    const stage = e.target.getStage();
    if (!stage || e.target !== stage) return;
    setView((prev) => sanitizeView({ ...prev, x: stage.x(), y: stage.y() }));
  }, []);

  const onDragMove = useCallback((_e: KonvaEventObject<DragEvent>) => {
    // Intentionally left blank: Konva owns the position during drag; we sync
    // back on dragEnd. Defining this lets callers safely compose handlers.
  }, []);

  const isAdjusted =
    Math.abs(view.scale - 1) > 1e-3 ||
    Math.abs(view.x) > 0.5 ||
    Math.abs(view.y) > 0.5;

  return {
    stageRef,
    scale: view.scale,
    x: view.x,
    y: view.y,
    isAdjusted,
    reset,
    panBy,
    ensureVisible,
    isPinching,
    onWheel,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onDragEnd,
    onDragMove,
  };
}
